import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { ArrowRight, Loader2 } from "lucide-react";
import { WaitlistConfirmModal } from "./WaitlistConfirmModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WaitlistForm = ({ variant = "light", showTransition = false }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    transition: "",
    zip_code: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.zip_code || formData.zip_code.length !== 5) {
      toast.error(!formData.zip_code || formData.zip_code.length !== 5 
        ? "Please enter a valid 5-digit ZIP code" 
        : "Please fill in your name and email");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, formData);
      setFormData({ name: "", email: "", transition: "", zip_code: "" });
      setShowConfirm(true);
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDark = variant === "dark";

  return (
    <>
      <motion.form
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        onSubmit={handleSubmit}
        className="w-full max-w-xl mx-auto"
        data-testid="waitlist-form"
      >
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Input
              type="text"
              placeholder="Your name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`flex-1 h-12 rounded-full px-6 ${
                isDark
                  ? "bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-brand-peach"
                  : "bg-white border-gray-200 text-text-dark placeholder:text-gray-400 focus:border-brand-primary"
              }`}
              data-testid="waitlist-name-input"
            />
            <Input
              type="email"
              placeholder="Your email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className={`flex-1 h-12 rounded-full px-6 ${
                isDark
                  ? "bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-brand-peach"
                  : "bg-white border-gray-200 text-text-dark placeholder:text-gray-400 focus:border-brand-primary"
              }`}
              data-testid="waitlist-email-input"
            />
          </div>

          {showTransition && (
            <Input
              type="text"
              placeholder="What transition are you navigating? (optional)"
              value={formData.transition}
              onChange={(e) => setFormData({ ...formData, transition: e.target.value })}
              className={`w-full h-12 rounded-full px-6 ${
                isDark
                  ? "bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-brand-peach"
                  : "bg-white border-gray-200 text-text-dark placeholder:text-gray-400 focus:border-brand-primary"
              }`}
              data-testid="waitlist-transition-input"
            />
          )}

          <Input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={5}
            placeholder="ZIP Code"
            value={formData.zip_code}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '').slice(0, 5);
              setFormData({ ...formData, zip_code: val });
            }}
            className={`w-full sm:w-48 h-12 rounded-full px-6 ${
              isDark
                ? "bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:border-brand-peach"
                : "bg-white border-gray-200 text-text-dark placeholder:text-gray-400 focus:border-brand-primary"
            }`}
            data-testid="waitlist-zip-input"
          />

          <div className="flex justify-center">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto h-12 px-8 rounded-full font-semibold transition-all duration-300 bg-gradient-to-r from-brand-peach to-brand-lavender hover:shadow-[0_0_30px_rgba(245,168,130,0.5)] hover:-translate-y-0.5 text-deep-violet"
              data-testid="waitlist-submit-btn"
            >
              {isSubmitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Join Waitlist
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </div>
          <p className={`text-center text-xs leading-relaxed ${isDark ? "text-white/40" : "text-gray-400"}`} data-testid="waitlist-consent-text">
            By submitting, you agree to our <a href="/privacy" className={`underline ${isDark ? "hover:text-white/60" : "hover:text-gray-600"}`}>Privacy Policy</a> and <a href="/terms" className={`underline ${isDark ? "hover:text-white/60" : "hover:text-gray-600"}`}>Terms of Service</a>. We'll only use your information to contact you about Become. We never sell your data.
          </p>
        </div>
      </motion.form>

      <WaitlistConfirmModal isOpen={showConfirm} onClose={() => setShowConfirm(false)} />
    </>
  );
};
